import type { ServerFrame } from "./frames.ts";

/**
 * The bounded client-side pause buffer (design 12.3, FR-9.3 / NFR-4).
 *
 * Live-tail pause is a PRESENTATION state, not an ingest pause: the stream keeps flowing while the
 * operator studies a frozen view. So while paused the client does not fold deltas into the visible
 * view; it parks them here, in a buffer with a hard bound, and shows the buffered-event count. On
 * resume WITHOUT overflow the parked deltas are replayed in order, then live resumes. If the bound
 * is exceeded the buffer OVERFLOWS: it stops keeping deltas, remembers that a gap happened, and the
 * client — on resume — asks the hub for a fresh snapshot and discloses the gap rather than replaying
 * an incomplete range ("unverifiable is never green", design 12.3).
 *
 * This module is pure and immutable so it is trivial to reason about and test: every operation
 * returns the next buffer. Snapshots are never buffered (the client applies a snapshot to the view
 * immediately even while paused, because a snapshot replaces state and is the recovery path); only
 * deltas are parked here.
 */

export interface PauseBuffer {
  /** Parked delta frames, in arrival order. Empty once drained or after overflow. */
  readonly frames: readonly ServerFrame[];
  /** The hard cap on parked frames. */
  readonly limit: number;
  /**
   * True once more deltas arrived than the buffer could hold. Latches until the buffer is reset:
   * an overflow is unrecoverable by replay, so the only honest recovery is a fresh snapshot.
   */
  readonly overflowed: boolean;
}

/** The hub keeps a 2,000-event ring, so a pause buffer of the same order is a sensible default. */
export const DEFAULT_PAUSE_BUFFER_LIMIT = 2000;

export function createPauseBuffer(limit: number = DEFAULT_PAUSE_BUFFER_LIMIT): PauseBuffer {
  return { frames: [], limit: Math.max(1, Math.floor(limit)), overflowed: false };
}

/** The buffered-event count the chrome/Events screen displays. */
export function bufferedCount(buffer: PauseBuffer): number {
  return buffer.frames.length;
}

/**
 * Park one delta frame. Returns the next buffer. Once overflowed, further frames are dropped and the
 * overflow stays latched (there is no point keeping a partial tail of the missed range). Adding the
 * frame that would exceed the limit sets `overflowed` and discards the incomplete range.
 */
export function enqueue(buffer: PauseBuffer, frame: ServerFrame): PauseBuffer {
  if (buffer.overflowed) return buffer;
  if (buffer.frames.length + 1 > buffer.limit) {
    // The incomplete range is discarded on overflow; recovery is a fresh snapshot, not a replay.
    return { ...buffer, frames: [], overflowed: true };
  }
  return { ...buffer, frames: [...buffer.frames, frame] };
}

/**
 * Drain the buffer for an ordered replay. Returns the parked frames (in arrival order) and a fresh
 * empty buffer. Caller folds the returned frames into the view with the reducer, in order.
 */
export function drain(buffer: PauseBuffer): { frames: readonly ServerFrame[]; next: PauseBuffer } {
  return { frames: buffer.frames, next: createPauseBuffer(buffer.limit) };
}

/** Reset to an empty, non-overflowed buffer (e.g. after a fresh snapshot settled the gap). */
export function reset(buffer: PauseBuffer): PauseBuffer {
  return createPauseBuffer(buffer.limit);
}
